import React from 'react';

/**
 * The top of a page: its title, one sentence on what the page is for, and its actions.
 *
 * Until 2026-09-10 every page wrote its own: six layouts across 35 pages, titles from 14 to 24px in
 * five colours, and four pages with no heading element at all. The title is the sidebar label
 * (Preet, 2026-09-10), so the word someone clicked is the word they land on.
 *
 * It imports nothing from react-router. Page tests mock the router only in part, so a back link is
 * built by the page, which already has `Link` or `navigate`, and handed in as `back`.
 */
export interface PageHeaderProps {
  title: React.ReactNode;
  /** One sentence on what the page is for. */
  subtitle?: React.ReactNode;
  /** Before the title, e.g. a back link. */
  back?: React.ReactNode;
  /** Beside the title, e.g. <ModuleInfoButton />. */
  info?: React.ReactNode;
  /** The right-hand side. At most one primary button. */
  actions?: React.ReactNode;
  /** A row under the title, for controls that belong to the whole page. */
  children?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, back, info, actions, children }) => (
  <header className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {back}
        {/* On a phone the title wraps: truncating it beside a Back button left it two letters wide. */}
        <h1
          className="min-w-0 max-w-full break-words text-xl font-semibold leading-7 text-slate-900 sm:truncate"
          title={typeof title === 'string' ? title : undefined}
        >
          {title}
        </h1>
        {info}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
    {subtitle ? <p className="mt-1 max-w-[80ch] text-xs text-slate-500">{subtitle}</p> : null}
    {children ? <div className="mt-3">{children}</div> : null}
  </header>
);

export default PageHeader;
